import { useState, useEffect, useMemo, useCallback } from "react";
import { Spin, message, Tabs } from "antd";
import { Row, Col } from "react-bootstrap"
import axiosService from "../../../utils/axios.service";
import _ from "lodash";
import { format, startOfMonth } from "date-fns"
import { Tag } from "rsuite";
import styles from "../style.module.css"
import Filter from "./items/Filter"
import DataByDate from "./items/DataByDate"
import DataByProduct from "./items/DataByProduct"

const Freedom = () => {
    const [loading, setLoading] = useState(true);

    const [storeSelected, setStoreSelected] = useState([]);
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

    const [netByDate, setNetByDate] = useState([]);
    const [netByProduct, setNetByProduct] = useState([]);
    const [actualByDate, setActualByDate] = useState([]);
    const [actualByProduct, setActualByProduct] = useState([]);
    
    const [listStore, setListStore] = useState([]);

    const [sumNet, setSumNet] = useState([]);
    const [sumActual, setSumActual] = useState([]);
    
    const fetchData = useCallback(async (stores, signal, startDate, endDate) => {
        try {
            setLoading(true)
            const res = await axiosService(
                "api/reports/data-pillar",
                "POST",
                {
                    startDate: startDate,
                    endDate: endDate,
                    stores,
                    type: 1,
                    pillar: "2"
                },
                { signal }
            );

            if (res.data.success) {
                setNetByDate(res.data.data.netRevenue.netByDate || []);
                setNetByProduct(res.data.data.netRevenue.netByProduct || []);
                
                setActualByDate(res.data.data.actualRevenue.actualByDate || []);
                setActualByProduct(res.data.data.actualRevenue.actualByProduct || []);

                setListStore(res.data.data.listStore)
                setSumNet(buildSum('revenue', res.data.data.netRevenue.netByDate, res.data.data.listStore));
                setSumActual(buildSum('revenue', res.data.data.actualRevenue.actualByDate, res.data.data.listStore));
            }
        } catch (err) {
            if (err.name !== "CanceledError") {
                message.error("Lỗi tải dữ liệu");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const debouncedFetch = useMemo(() => {
        return _.debounce((stores, signal, startDate, endDate) => {
            fetchData(stores, signal, startDate, endDate);
        }, 1000);
    }, [fetchData]);

    useEffect(() => {
        if (!storeSelected.length) return;

        const controller = new AbortController();

        debouncedFetch(storeSelected, controller.signal, startDate, endDate);

        return () => {
            controller.abort();
            debouncedFetch.cancel();
        };
    }, [storeSelected, debouncedFetch, startDate, endDate]);

    const handleExport = async () => {
        try {
            setLoading(true)
            const res = await axiosService(
                "api/reports/data-pillar",
                "POST",
                {
                    startDate: startDate,
                    endDate: endDate,
                    stores: storeSelected,
                    type: 2,
                    pillar: "2"
                }
            );

            if (res.data.success) {
                window.location.href = res.data.data
            }
        } catch (err) {
            message.error("Lỗi tải dữ liệu");
        } finally {
            setLoading(false);
        }
    }

    const buildSum = (type, data, listStore) => {
        return listStore.map(store => ({
            id: store.id,
            name: store.name_store,
            value: data.reduce((sum, item) => {
                return sum + (item[`${store.id}_${type}`] || 0);
            }, 0)
        }));
    };

    const itemsTab = [
        {
            key: '1',
            label: 'Doanh số thuần',
            children: (
                <DataByDate data={{result: netByDate, listStore}} />
            ),
        },
        {
            key: '2',
            label: 'Doanh số thuần - Dịch vụ',
            children: (<DataByProduct data={{result: netByProduct, listStore}} />),
        },
        {
            key: '4',
            label: 'Thực thu',
            children: (
                <DataByDate data={{result: actualByDate, listStore}} />
            ),
        },
        {
            key: '5',
            label: 'Thực thu - Dịch vụ',
            children: (
                <DataByProduct data={{result: actualByProduct, listStore}} />
            ),
        }
    ];

    const convertCurrency = (data) => {
        let res = new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(data)
        return res;
    };

    return (
        <div className={`${styles.background} report-page container-fluid`} style={{ width: "100%" }}>
            <Spin spinning={loading} tip="Đang tải...">
                <Filter 
                    infoPage={{title: "Tự do", type: "freedom"}}
                    storeSelected={storeSelected}
                    setStoreSelected={setStoreSelected}
                    startDate={startDate} 
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    handleExport={handleExport}
                    axiosService={axiosService}
                />
                <Row>
                    <Col xs={12} md={4} lg={3} className="mb-2">
                        <Tag className="w-100 h-100 p-2" style={{background: '#ff6513'}}>
                            <div className="d-flex justify-content-between">
                                <p className={styles.textTag2}><b>Doanh số thuần</b></p>
                                <p className={styles.textTag2}><b>{convertCurrency(_.sumBy(sumNet, "value") ?? 0)}</b></p>
                            </div>
                            <div className="p-1">
                                {sumNet.map((x,y) => {
                                    return (
                                        <div className="d-flex justify-content-between" key={y}>
                                            <p className={styles.textTag3}>{x.name}</p> 
                                            <p className={styles.textTag3}>{convertCurrency(x.value)}</p>
                                        </div>
                                    )
                                })}
                            </div>
                        </Tag>
                    </Col>
                    <Col xs={12} md={4} lg={3} className="mb-2">
                        <Tag className="w-100 h-100 p-2" style={{background: 'rgb(25, 119, 243)'}}>
                            <div className="d-flex justify-content-between">
                                <p className={styles.textTag2}><b>Thực thu</b></p>
                                <p className={styles.textTag2}><b>{convertCurrency(_.sumBy(sumActual, "value"))}</b></p>
                            </div>

                            <div className="p-1">
                                {sumActual.map((x,y) => {
                                    return (
                                        <div className="d-flex justify-content-between" key={y}>
                                            <p className={styles.textTag3}>{x.name}</p> 
                                            <p className={styles.textTag3}>{convertCurrency(x.value)}</p>
                                        </div>
                                    )
                                })}
                            </div>
                        </Tag>
                    </Col>
                </Row>
                <Tabs defaultActiveKey="1" items={itemsTab} />
            </Spin>
        </div>
    );
};

export default Freedom;